import React, { useEffect, useRef, useState } from "react";
import AOS from "aos";
import { motion } from "framer-motion";
import "aos/dist/aos.css";

// Vanta.js + Three.js
import NET from "vanta/dist/vanta.net.min";
import * as THREE from "three";

const PythonFullStack = () => {
  const vantaRef = useRef(null);
  const [vantaEffect, setVantaEffect] = useState(null);

  useEffect(() => {
    // Initialize AOS
    AOS.init({ duration: 1200, once: true, offset: 100 });

    // Initialize Vanta background
    if (!vantaEffect) {
      setVantaEffect(
        NET({
          el: vantaRef.current,
          THREE: THREE,
          mouseControls: true,
          touchControls: true,
          minHeight: 200.0,
          minWidth: 200.0,
          scale: 1.0,
          scaleMobile: 1.0,
          color: 0x4f46e5, // Indigo
          backgroundColor: 0xf9fafb, // Light gray
          points: 12.0,
          maxDistance: 20.0,
          spacing: 18.0,
        })
      );
    }

    return () => {
      if (vantaEffect) vantaEffect.destroy();
    };
  }, [vantaEffect]);

  return (
    <section
      ref={vantaRef}
      className="relative py-20 px-6 md:px-20 overflow-hidden"
    >
      {/* Overlay to keep text readable */}
      <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-0"></div>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Header Section */}
        <div
          className="max-w-6xl mx-auto bg-white shadow-xl rounded-3xl p-10 flex flex-col md:flex-row items-center gap-10 hover:shadow-2xl transition-shadow"
          data-aos="fade-up"
        >
          <motion.img
            src="/assets/9f4981b3c65d265e371b078308e8e7eb.jpg"
            alt="Python Full Stack Banner"
            className="w-full md:w-1/2 rounded-2xl object-cover"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1 }}
          />
          <div className="md:w-1/2">
            <h1
              className="text-4xl font-bold text-indigo-700 mb-4"
              data-aos="fade-right"
            >
              Python Full Stack Development
            </h1>
            <p
              className="text-gray-700 leading-relaxed mb-4"
              data-aos="fade-left"
            >
              Become a professional Full Stack Developer using Python, React JS,
              Django, and Flask. This comprehensive course takes you from
              fundamentals to advanced web application development, including
              database management, API integration, and deployment to cloud
              platforms.
            </p>
            <motion.span
              className="inline-block bg-purple-100 text-purple-700 px-4 py-1 rounded-full font-semibold text-sm"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Beginner to Advanced
            </motion.span>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="max-w-6xl mx-auto mt-20 grid md:grid-cols-3 gap-10">
          {[
            {
              title: "Why Choose This Course?",
              text: "Learn from industry experts with hands-on projects. Gain real-world experience in full stack web development, master modern tools and frameworks, and build a portfolio that makes you job-ready.",
            },
            {
              title: "Roles You'll Master",
              text: "Backend Developer, Frontend Developer, API Specialist, Database Manager, DevOps Engineer.",
            },
            {
              title: "Responsibilities",
              text: "Build end-to-end applications, implement secure and scalable solutions, integrate third-party APIs, manage databases efficiently, and deploy applications on cloud platforms.",
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              className="bg-white p-6 rounded-2xl shadow-lg hover:scale-105 transition-transform"
              data-aos="flip-up"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.2 }}
            >
              <h3 className="text-2xl font-semibold text-indigo-700 mb-3">
                {item.title}
              </h3>
              <p className="text-gray-600">{item.text}</p>
            </motion.div>
          ))}
        </div>

        {/* What You Will Learn */}
        <div className="max-w-6xl mx-auto mt-20">
          <h2
            className="text-3xl font-bold text-center text-indigo-700 mb-12"
            data-aos="zoom-in"
          >
            What You Will Learn
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Frontend Development",
                text: "HTML, CSS, JavaScript, React JS, responsive UI design, component-based architecture, state management, and AOS animations.",
              },
              {
                title: "Backend Development",
                text: "Python fundamentals, Django, Flask, REST API creation, authentication, session management, and database integration (MySQL, PostgreSQL, MongoDB).",
              },
              {
                title: "Deployment & DevOps",
                text: "Git version control, CI/CD pipelines, Docker basics, cloud hosting (AWS, Heroku), monitoring, and production deployment strategies.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="bg-gradient-to-r from-indigo-100 to-purple-100 p-6 rounded-2xl shadow-lg hover:scale-105 transition-transform"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.2 }}
              >
                <h3 className="text-xl text-black font-semibold mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-700">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Career Path */}
        <div className="max-w-6xl mx-auto mt-20 grid md:grid-cols-2 gap-10">
          <motion.div
            className="bg-white p-6 rounded-2xl shadow-lg hover:scale-105 transition-transform"
            data-aos="fade-right"
            initial={{ x: -50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-2xl font-semibold text-indigo-700 mb-3">
              Career Opportunities
            </h3>
            <motion.ul
              className="list-disc list-inside text-gray-700"
              initial="hidden"
              whileInView="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.2 },
                },
              }}
            >
              {[
                "Full Stack Developer",
                "Frontend Developer (React JS)",
                "Backend Developer (Python, Django)",
                "API Developer",
                "DevOps & Deployment Engineer",
              ].map((role, i) => (
                <motion.li
                  key={i}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                >
                  {role}
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
          <div
            className="bg-white p-6 rounded-2xl shadow-lg hover:scale-105 transition-transform"
            data-aos="fade-left"
          >
            <h3 className="text-2xl font-semibold text-indigo-700 mb-3">
              Technologies Covered
            </h3>
            <p className="text-gray-700 mb-2">
              Python, Django, Flask, React JS, HTML, CSS, JavaScript, MySQL,
              PostgreSQL, MongoDB, Git, Docker, AWS, Heroku.
            </p>
            <p className="text-gray-700">
              By the end of the course, you'll have a strong foundation to build
              real-world, production-ready web applications.
            </p>
          </div>
        </div>

        {/* Call to Action */}
        <motion.div
          className="max-w-4xl mx-auto mt-20 text-center"
          data-aos="zoom-in"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <h2 className="text-3xl font-bold text-indigo-700 mb-4">
            Ready to Start Your Python Full Stack Journey?
          </h2>
          <p className="text-gray-700 mb-6">
            Join today and transform your career with hands-on projects and
            expert guidance.
          </p>
          <motion.button
            whileHover={{ scale: 1.1, rotate: 2 }}
            whileTap={{ scale: 0.95 }}
            className="bg-indigo-700 text-white px-8 py-3 rounded-full font-semibold hover:bg-indigo-800 transition-colors"
          >
            Enroll Now
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default PythonFullStack;
